import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "sonner";
import "@/App.css";

import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Exercises from "./pages/Exercises";
import UserProfile from "./pages/UserProfile";
import Onboarding from "./pages/Onboarding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PublicRoutine from "./pages/PublicRoutine";
import AthleteCard from "./pages/AthleteCard";
import Payments from "./pages/Payments";
import AdminErrors from "./pages/AdminErrors";
import Neuroscience from "./pages/Neuroscience";
import Logo from "./components/Logo";
import { Barbell } from "@phosphor-icons/react";

function ProtectedRoute({ children, adminOnly = false, staffOnly = false, skipOnboarding = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Logo size={120} className="animate-pulse" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (staffOnly && user.role !== "admin" && user.role !== "profesor") return <Navigate to="/dashboard" replace />;
  // Force non-admin users to complete onboarding before using the app
  if (!skipOnboarding && user.role !== "admin" && user.onboarded === false) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function ProfileMeRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
        <Logo size={120} className="animate-pulse" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Navigate to={`/profile/${user.user_id}${location.search}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Listen for push notification clicks from the service worker (when app is already open)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "notification-click" && event.data.url) {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);

  // Handle OAuth callback — reactive to navigation so it unmounts after auth completes
  if (location.hash?.includes("session_id=") || window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:token" element={<PublicRoutine />} />
      <Route path="/athlete/:token" element={<AthleteCard />} />
      <Route path="/onboarding" element={<ProtectedRoute skipOnboarding><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute staffOnly><Users /></ProtectedRoute>} />
      <Route path="/exercises" element={<ProtectedRoute staffOnly><Exercises /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
      <Route path="/neurociencia" element={<ProtectedRoute><Neuroscience /></ProtectedRoute>} />
      <Route path="/admin/errors" element={<ProtectedRoute adminOnly><AdminErrors /></ProtectedRoute>} />
      <Route path="/profile/me" element={<ProfileMeRedirect />} />
      <Route path="/profile/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
